import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useResponseNotification from 'hooks/useResponseNotification';
import { ListPayload } from 'core/http/response';
import {
    applyItemFieldTemplate,
    archiveItemFieldTemplate,
    createItemFieldTemplate,
    deleteItemFieldTemplate,
    duplicateItemFieldTemplate,
    getItemFieldTemplate,
    listItemFieldTemplates,
    unarchiveItemFieldTemplate,
    updateItemFieldTemplate,
} from 'store/item-field-templates/client';
import {
    ItemFieldTemplate,
    ItemFieldTemplateFilters,
    ItemFieldTemplateRequest,
} from 'store/item-field-templates/itemFieldTemplate';
import {
    applyItemFieldTemplateNotifs,
    archiveItemFieldTemplateNotifs,
    duplicateItemFieldTemplateNotifs,
    saveItemFieldTemplateNotifs,
    unarchiveItemFieldTemplateNotifs,
} from 'utils/notificationMessages';

interface ListTemplatesParams extends ItemFieldTemplateFilters {
    pageNumber: number;
    pageSize: number;
    query?: string;
    archived?: boolean;
}

const didSucceed = (request: Promise<unknown>) =>
    request.then(
        () => true,
        () => false
    );

const useItemFieldTemplates = () => {
    const [templates, setTemplates] = useState<ListPayload<ItemFieldTemplate>>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown>();
    const { withResponseNotifications } = useResponseNotification();
    const { t } = useTranslation('common');

    const listTemplates = useCallback(
        async ({ pageNumber, pageSize, query, archived = false, ...filters }: ListTemplatesParams) => {
            setIsLoading(true);
            setError(undefined);
            try {
                const payload = await listItemFieldTemplates({
                    ...filters,
                    page: pageNumber,
                    size: pageSize,
                    query,
                    archived,
                });
                setTemplates(payload);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    const saveWithNotifications = useCallback(
        async (save: Promise<ItemFieldTemplate>) => {
            const saved = await save.catch(() => undefined);
            return withResponseNotifications(saved, saveItemFieldTemplateNotifs(t));
        },
        [withResponseNotifications, t]
    );

    const createTemplate = useCallback(
        (template: ItemFieldTemplateRequest) => saveWithNotifications(createItemFieldTemplate(template)),
        [saveWithNotifications]
    );

    const editTemplate = useCallback(
        (templateId: string, template: ItemFieldTemplateRequest) =>
            saveWithNotifications(updateItemFieldTemplate(templateId, template)),
        [saveWithNotifications]
    );

    const duplicateTemplate = useCallback(
        async (templateId: string, name: string) => {
            const duplicated = await didSucceed(duplicateItemFieldTemplate(templateId, { name }));
            return withResponseNotifications(duplicated, duplicateItemFieldTemplateNotifs(t));
        },
        [withResponseNotifications, t]
    );

    const archiveTemplate = useCallback(
        async (templateId: string) => {
            const archived = await didSucceed(archiveItemFieldTemplate(templateId));
            return withResponseNotifications(archived, archiveItemFieldTemplateNotifs(t));
        },
        [withResponseNotifications, t]
    );

    const unarchiveTemplate = useCallback(
        async (templateId: string) => {
            const unarchived = await didSucceed(unarchiveItemFieldTemplate(templateId));
            return withResponseNotifications(unarchived, unarchiveItemFieldTemplateNotifs(t));
        },
        [withResponseNotifications, t]
    );

    const applyTemplate = useCallback(
        async (declarationId: string, templateId: string, itemIds: string[]) => {
            const applied = await didSucceed(applyItemFieldTemplate(declarationId, { templateId, itemIds }));
            return withResponseNotifications(applied, applyItemFieldTemplateNotifs(t));
        },
        [withResponseNotifications, t]
    );

    return {
        templates,
        isLoading,
        error,
        listTemplates,
        getTemplate: getItemFieldTemplate,
        createTemplate,
        editTemplate,
        duplicateTemplate,
        archiveTemplate,
        unarchiveTemplate,
        deleteTemplate: deleteItemFieldTemplate,
        applyTemplate,
    };
};

export default useItemFieldTemplates;
